import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiMapPin, FiUsers, FiFileText, FiCheckCircle, FiClock } from 'react-icons/fi';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import AdminLayout from './AdminLayout';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    showrooms: 0,
    salesPersons: 0,
    warranties: 0
  });
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  const parseList = (data, key) => {
    if (Array.isArray(data)) return data;
    if (key && Array.isArray(data?.[key])) return data[key];
    if (Array.isArray(data?.data)) return data.data;
    return [];
  };

  const fetchStats = useCallback(async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [showroomsRes, salesRes, warrantiesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/showrooms`, config),
        axios.get(`${API_BASE_URL}/api/users/sales-persons`, config),
        axios.get(`${API_BASE_URL}/api/warranty-registrations`, config),
      ]);

      setStats({
        showrooms: parseList(showroomsRes.data, 'showrooms').length,
        salesPersons: parseList(salesRes.data, 'data').length,
        warranties: parseList(warrantiesRes.data, 'warrantyRegistrations').length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStats();
  }, [token, navigate, fetchStats]);

  const statCards = [
    { 
      title: 'Total Showrooms', 
      value: stats.showrooms, 
      icon: FiMapPin, 
      color: 'bg-blue-500',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      path: '/admin/showrooms'
    },
    { 
      title: 'Sales Persons', 
      value: stats.salesPersons, 
      icon: FiUsers, 
      color: 'bg-green-500',
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      path: '/admin/users'
    },
    { 
      title: 'Warranties Registered', 
      value: stats.warranties, 
      icon: FiCheckCircle, 
      color: 'bg-purple-500',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600',
      path: '/admin/warranties'
    },
    { 
      title: 'Pending Actions', 
      value: '0', 
      icon: FiClock, 
      color: 'bg-orange-500',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-600',
      path: '#'
    },
  ];

  const menuItems = [
    { 
      title: 'Showrooms', 
      icon: FiMapPin, 
      description: 'Manage showroom locations and details', 
      path: '/admin/showrooms', 
      color: 'bg-blue-100 text-blue-700' 
    },
    { 
      title: 'Sales Persons', 
      icon: FiUsers, 
      description: 'Manage sales staff information', 
      path: '/admin/users', 
      color: 'bg-green-100 text-green-700' 
    },
    { 
      title: 'Warranty Registrations', 
      icon: FiFileText, 
      description: 'View and manage all warranty claims', 
      path: '/admin/warranties', 
      color: 'bg-purple-100 text-purple-700' 
    },
  ];

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="mb-8">
          <h1 className="page-header">Dashboard</h1>
          <p className="text-gray-500 mt-1 text-sm">Welcome back! Here's what's happening with your warranty system.</p>
        </div>

        {/* Stats Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map((stat, index) => (
              <div
                key={index}
                onClick={() => stat.path !== '#' && navigate(stat.path)}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${stat.path !== '#' ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                  </div>
                  <div className={`${stat.bgColor} p-3 rounded-full`}>
                    <stat.icon className={`${stat.textColor} text-xl`} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Actions Menu */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {menuItems.map((item, index) => (
            <div
              key={index}
              onClick={() => navigate(item.path)}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 cursor-pointer hover:shadow-md transition-all hover:scale-105"
            >
              <div className={`${item.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                <item.icon className="text-2xl" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Recent Activity Section */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-base font-semibold text-syakar-navy mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <p className="text-gray-500 text-center py-4">No recent activity to display</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;